/**
 * List config for Settings › Accounts — the sixth consumer of the shared list stack
 * (`useListControls` + `ListToolbar` + `filterSortItems`), after audiences, activations, dashboards,
 * workflows and the context hub.
 *
 * Everything here is display-agnostic: the card and the table both read these helpers, so a rename
 * or a relabel lands in both at once rather than being restated per renderer.
 */
import type { RouterClient } from '@orpc/server';
import type { AppRouter } from '@server/rpc';

import type { FilterGroup } from '@/_shared/components/list/ListToolbar';
import type { ListDeriveConfig, SortState } from '@/_shared/lib/list-controls';

import { connectedAsLabel } from '../components/rudderstackAccount/oauthIdentity';

/** One account as `list` returns it — derived from the router, never re-declared. */
export type AccountListItem = Awaited<
  ReturnType<RouterClient<AppRouter>['settings']['rudderstackAccounts']['list']>
>['accounts'][number];

export type AccountAuthLabel = 'OAuth' | 'API key' | 'Unknown';

/**
 * A list row: the wire shape plus the labels and the per-row flag the table and the cards render.
 *
 * `AccountListRow`, not `AccountRow` — `AccountRow` is a COMPONENT in
 * `audiences/components/accountField/AccountRow.tsx`, so one name meant both a renderer and a shape
 * depending on which file you were in, and neither grep nor auto-import could tell them apart.
 */
export interface AccountListRow extends AccountListItem {
  typeLabel: string;
  authLabel: AccountAuthLabel;
}

/** ACT2-1124: an account of a readable workspace, as `accountCopies.listOptions` answers it. */
export type OrganizationAccountOption = Awaited<
  ReturnType<RouterClient<AppRouter>['accountCopies']['listOptions']>
>['accounts'][number];

/**
 * ACT2-1124: an activation account of a RudderStack workspace that Lookout can read (Settings › General). Copy makes it
 * one of this workspace's own accounts.
 */
export interface OrganizationAccountListRow {
  /** Never an id of this workspace's own accounts: those are RudderStack account ids. */
  id: string;
  name: string;
  typeLabel: string;
  authLabel: AccountAuthLabel;
  destinationType: string;
  option: OrganizationAccountOption;
}

/** A row of Settings › Accounts: an account of this workspace, or an account of a readable workspace. */
export type AccountsListEntry = AccountListRow | OrganizationAccountListRow;

export function isOrganizationAccountRow(row: AccountsListEntry): row is OrganizationAccountListRow {
  return 'option' in row;
}

/** What a row of a readable workspace reads from the account definition Lookout offers. */
interface OfferedDefinition {
  destinationType: string;
  destinationDisplayName: string;
  authenticationType: string;
}

/**
 * The activation accounts of the readable workspaces that Settings › Accounts lists: only of a definition that
 * Lookout offers for a new account, and not one whose copy is already among this workspace's own accounts (that row
 * stands in for it), as the Warehouses tab does.
 */
export function organizationAccountRows(
  options: readonly OrganizationAccountOption[],
  offeredDefinitions: ReadonlyMap<string, OfferedDefinition>,
  ownAccountIds: ReadonlySet<string>,
): OrganizationAccountListRow[] {
  return options.flatMap((option) => {
    const definition = option.definitionName === null ? undefined : offeredDefinitions.get(option.definitionName);
    if (!definition) return [];
    if (option.copyAccountId !== null && ownAccountIds.has(option.copyAccountId)) return [];
    return [
      {
        id: `organization:${option.accountId}`,
        name: option.name,
        typeLabel: definition.destinationDisplayName,
        authLabel: accountAuthLabel(definition.authenticationType),
        destinationType: definition.destinationType,
        option,
      },
    ];
  });
}

/**
 * A row that is actually being RENDERED, carrying the transient per-row dialog flag.
 *
 * Separate from `AccountListRow` because the flag must not reach the filter/sort pipeline: it
 * changes on every open and close of the re-authorize dialog, and `filterSortItems` keyed on an
 * array that changed identity for that reason re-derived every row's search text and re-sorted the
 * list — and, worse, gave `Table` a new `data` identity, which resets its scroll to the top and (past
 * its 100-row virtualization threshold) unmounted the very row the dialog belonged to.
 *
 * The flag rides the row DATUM rather than a closure in the name column, and that half IS
 * load-bearing: `Table` derives its column defs from the `columns` array's identity and `flexRender`
 * uses each `cell` as a React element TYPE, so a rebuilt `columns` remounts every cell — and an OAuth
 * flow remounted mid-consent loses its popup, its nonce and its completion listener silently.
 */
export interface AccountTableRow extends AccountListRow {
  confirmingReauth: boolean;
}

/**
 * The platform's auth type, as a person reads it.
 *
 * `'unknown'` is its own case rather than falling into "API key": it means the catalogue no longer
 * lists the definition, and calling that an API key would be an assertion the row cannot make.
 */
export function accountAuthLabel(authenticationType: string): AccountAuthLabel {
  if (authenticationType === 'oauth') return 'OAuth';
  if (authenticationType === 'unknown') return 'Unknown';
  return 'API key';
}

/** The "Connected as …" line, for OAuth accounts only — null everywhere else. */
export function accountConnectedAs(
  account: Pick<AccountListItem, 'authenticationType' | 'oauthIdentity'>,
): string | null {
  return account.authenticationType === 'oauth' ? connectedAsLabel(account.oauthIdentity) : null;
}

export type AccountSortKey = 'name' | 'type' | 'updated';
export type AccountFilterKey = 'type' | 'auth';

export const ACCOUNT_SORT_KEYS: readonly AccountSortKey[] = ['name', 'type', 'updated'];
export const ACCOUNT_FILTER_KEYS: readonly AccountFilterKey[] = ['type', 'auth'];

/**
 * Name-ascending, not recently-updated.
 *
 * Every other list here defaults to recency because its items are WORK — an audience you were just
 * editing is the one you want on top. Accounts are infrastructure: you arrive knowing which one you
 * came for, and alphabetical is the order that lets you find it. It is also the order the table had
 * before this toolbar existed, so nobody's list reshuffles on upgrade.
 */
export const ACCOUNT_DEFAULT_SORT: SortState<AccountSortKey> = { key: 'name', dir: 'asc' };

export const ACCOUNT_SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'type', label: 'Type' },
  { value: 'updated', label: 'Recently updated' },
] as const;

export const ACCOUNT_AUTH_OPTIONS = [
  { value: 'OAuth', label: 'OAuth' },
  { value: 'API key', label: 'API key' },
  { value: 'Unknown', label: 'Unknown' },
] as const;

/**
 * The Type filter's options, built from the accounts ACTUALLY on screen rather than from the whole
 * definition catalogue.
 *
 * Two reasons. The catalogue is every destination RudderStack offers — a hundred-odd entries, almost
 * none of which this workspace has an account for — and a filter listing values that match nothing is
 * a menu of dead ends. And the values are display LABELS, not definition names, so the several
 * definitions that serve one destination (an OAuth variant and a key-based one) collapse into the one
 * row a person means when they say "my Facebook accounts".
 */
export function accountTypeFilterOptions(rows: readonly AccountsListEntry[]): { value: string; label: string }[] {
  return [...new Set(rows.map((r) => r.typeLabel))]
    .sort((a, b) => a.localeCompare(b))
    .map((t) => ({ value: t, label: t }));
}

export function accountFilterGroups(rows: readonly AccountsListEntry[]): FilterGroup[] {
  const types = accountTypeFilterOptions(rows);
  const groups: FilterGroup[] = [];
  // Omitted entirely below two types: a single-option filter can only ever be a no-op or a way to
  // hide nothing, and an empty one is worse.
  if (types.length > 1) groups.push({ key: 'type', label: 'Type', options: types });
  const auths = ACCOUNT_AUTH_OPTIONS.filter((o) => rows.some((r) => r.authLabel === o.value));
  if (auths.length > 1) groups.push({ key: 'auth', label: 'Auth', options: [...auths] });
  return groups;
}

/** Ascending; `filterSortItems` applies the direction. */
const byText = (a: string, b: string): number => a.localeCompare(b);

const updatedAtOf = (row: AccountsListEntry): string => (isOrganizationAccountRow(row) ? '' : row.updatedAt);

export const ACCOUNT_DERIVE_CONFIG: ListDeriveConfig<AccountsListEntry, AccountSortKey, AccountFilterKey> = {
  /**
   * An ARRAY, so a query has to land inside one field rather than straddling two. "facebook prod"
   * therefore finds an account NAMED that, and does not match a Facebook-typed account called "prod"
   * — which is the distinction that makes the search useful on a workspace holding several accounts
   * per destination.
   *
   * `externalId` is in here for the same reason the table renders it: without the platform's own id,
   * two accounts a customer named the same are indistinguishable, and this is the surface that edits
   * and deletes them. An account of a readable workspace is found by its workspace's name too.
   */
  searchText: (a) =>
    isOrganizationAccountRow(a)
      ? [a.name, a.typeLabel, a.option.workspaceName]
      : [a.name, a.typeLabel, accountConnectedAs(a) ?? '', a.externalId ?? ''],
  comparators: {
    name: (a, b) => byText(a.name, b.name),
    // Ties broken by name: a workspace's accounts cluster hard by type, so an unbroken tie would
    // leave the whole Facebook block in whatever order the platform happened to return.
    type: (a, b) => byText(a.typeLabel, b.typeLabel) || byText(a.name, b.name),
    // An account of a readable workspace has no update time here, so it sorts as the oldest.
    updated: (a, b) => byText(updatedAtOf(a), updatedAtOf(b)),
  },
  filterPredicates: {
    type: (a, value) => a.typeLabel === value,
    auth: (a, value) => a.authLabel === value,
  },
};
